import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    PlusCircleIcon,
    PencilSquareIcon,
    TrashIcon,
    UserIcon,
    CurrencyDollarIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    CheckCircleIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

// --- START: MOCK DATA for Job Offers ---
const mockJobOffers = [
    {
        id: 1,
        candidateName: 'Jane Doe',
        position: 'Senior Software Engineer',
        salary: 120000,
        status: 'Sent',
        validUntil: '2025-10-15',
        sentDate: '2025-09-30',
    },
    {
        id: 2,
        candidateName: 'John Smith',
        position: 'Product Manager',
        salary: 105000,
        status: 'Accepted',
        validUntil: '2025-10-10',
        sentDate: '2025-09-25',
    },
    {
        id: 3,
        candidateName: 'Emily White',
        position: 'UX Designer',
        salary: 85000,
        status: 'Draft',
        validUntil: '2025-10-20',
        sentDate: null,
    },
];
// --- END: MOCK DATA ---

const JobOfferManagement = () => {
    const [jobOffers, setJobOffers] = useState(mockJobOffers);
    const [formData, setFormData] = useState({
        candidateName: '',
        position: '',
        salary: '',
        validUntil: '',
    });
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isEditing) {
            setJobOffers(jobOffers.map(offer =>
                offer.id === currentId ? { ...formData, id: currentId, sentDate: offer.sentDate, status: offer.status } : offer
            ));
            setIsEditing(false);
            setCurrentId(null);
        } else {
            const newOffer = {
                ...formData,
                id: jobOffers.length ? Math.max(...jobOffers.map(o => o.id)) + 1 : 1,
                status: 'Draft',
                sentDate: null,
            };
            setJobOffers([...jobOffers, newOffer]);
        }
        setFormData({ candidateName: '', position: '', salary: '', validUntil: '' });
    };

    const handleEdit = (offer) => {
        setFormData({
            candidateName: offer.candidateName,
            position: offer.position,
            salary: offer.salary,
            validUntil: offer.validUntil,
        });
        setIsEditing(true);
        setCurrentId(offer.id);
    };

    const handleDelete = (id) => {
        setJobOffers(jobOffers.filter(offer => offer.id !== id));
    };

    const handleSendOffer = (id) => {
        setJobOffers(jobOffers.map(offer =>
            offer.id === id ? { ...offer, status: 'Sent', sentDate: moment().format('YYYY-MM-DD') } : offer
        ));
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Accepted':
                return 'bg-green-100 text-green-800';
            case 'Sent':
                return 'bg-blue-100 text-blue-800';
            case 'Draft':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-200 p-8 text-gray-800">
            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-7xl mx-auto"
            >
                <h1 className="text-4xl font-extrabold text-purple-800 flex items-center mb-4">
                    <DocumentTextIcon className="h-10 w-10 text-purple-600 mr-4" />
                    Job Offer Management
                </h1>
                <p className="text-xl font-light text-purple-600 mb-8">
                    Create, track, and manage all job offers in one place.
                </p>

                {/* Create/Edit Job Offer Form */}
                <div className="bg-white p-8 rounded-3xl shadow-lg mb-8">
                    <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                        <PencilSquareIcon className="h-7 w-7 mr-2 text-gray-500" />
                        {isEditing ? 'Edit Job Offer' : 'Create New Job Offer'}
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="relative">
                                <label htmlFor="candidateName" className="block text-sm font-medium text-gray-700">Candidate Name</label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <UserIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                    </div>
                                    <input
                                        type="text"
                                        name="candidateName"
                                        id="candidateName"
                                        className="focus:ring-purple-500 focus:border-purple-500 block w-full pl-10 pr-4 py-2 sm:text-sm border-gray-300 rounded-md"
                                        placeholder="Enter candidate name"
                                        value={formData.candidateName}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="relative">
                                <label htmlFor="position" className="block text-sm font-medium text-gray-700">Position</label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <PencilSquareIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                    </div>
                                    <input
                                        type="text"
                                        name="position"
                                        id="position"
                                        className="focus:ring-purple-500 focus:border-purple-500 block w-full pl-10 pr-4 py-2 sm:text-sm border-gray-300 rounded-md"
                                        placeholder="Enter position"
                                        value={formData.position}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="relative">
                                <label htmlFor="salary" className="block text-sm font-medium text-gray-700">Annual Salary (INR)</label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <CurrencyDollarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                    </div>
                                    <input
                                        type="number"
                                        name="salary"
                                        id="salary"
                                        className="focus:ring-purple-500 focus:border-purple-500 block w-full pl-10 pr-4 py-2 sm:text-sm border-gray-300 rounded-md"
                                        placeholder="e.g., 90000"
                                        value={formData.salary}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="relative">
                                <label htmlFor="validUntil" className="block text-sm font-medium text-gray-700">Valid Until</label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                    </div>
                                    <input
                                        type="date"
                                        name="validUntil"
                                        id="validUntil"
                                        className="focus:ring-purple-500 focus:border-purple-500 block w-full pl-10 pr-4 py-2 sm:text-sm border-gray-300 rounded-md"
                                        value={formData.validUntil}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end space-x-4">
                            <button
                                type="submit"
                                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
                            >
                                {isEditing ? 'Save Changes' : 'Create Offer'}
                                <PlusCircleIcon className="-mr-1 ml-3 h-5 w-5" />
                            </button>
                        </div>
                    </form>
                </div>

                {/* Job Offers Table */}
                <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidate</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salary</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valid Until</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent On</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {jobOffers.length > 0 ? (
                                    jobOffers.map(offer => (
                                        <tr key={offer.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{offer.candidateName}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{offer.position}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">₹{offer.salary.toLocaleString('en-IN')}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(offer.status)}`}>
                                                    {offer.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{moment(offer.validUntil).format('MMM D, YYYY')}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{offer.sentDate ? moment(offer.sentDate).format('MMM D, YYYY') : 'N/A'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                {offer.status === 'Draft' && (
                                                    <button
                                                        onClick={() => handleSendOffer(offer.id)}
                                                        className="text-blue-600 hover:text-blue-900 mr-2"
                                                        title="Send Offer"
                                                    >
                                                        <EnvelopeIcon className="h-5 w-5" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleEdit(offer)}
                                                    className="text-indigo-600 hover:text-indigo-900 mr-2"
                                                    title="Edit"
                                                >
                                                    <PencilSquareIcon className="h-5 w-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(offer.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Delete"
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-4 text-center text-gray-500">No job offers found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default JobOfferManagement;